import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Card, List, Tag, Typography, Empty, Spin, Button, Select, Space, Image, Modal } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useSocketContext } from '../../sockets/SocketContext';
import { GlobalSearchResultReport } from '../TZ/globalSearchRes/GlobalSearchReport';
import {
  GLOBAL_SEARCH_PIPELINE_LABELS,
  formatGlobalSearchDate,
  pickYandexImageSearchRequest,
  extractGlobalSearchResultPayload,
  hasGlobalSearchReportContent,
} from '../../utils/globalSearchUi';
import {
  detectRequestTypeFromDescription,
  formatRequestTypeDetailed,
  getGroupLabelFromTasks,
} from '../../utils/requestTypeLabels';

const { Text } = Typography;

const detectMimeFromBase64 = (b64) => {
  if (!b64 || typeof b64 !== 'string') return 'image/jpeg';
  if (b64.startsWith('data:')) return null; // already full data URL
  if (b64.startsWith('/9j/')) return 'image/jpeg';
  if (b64.startsWith('iVBOR')) return 'image/png';
  if (b64.startsWith('R0lGOD')) return 'image/gif';
  if (b64.startsWith('Qk')) return 'image/bmp';
  return 'image/jpeg';
};

const looksLikeBase64 = (val) => {
  if (typeof val !== 'string') return false;
  if (val.startsWith('data:image/')) return true;
  return /[A-Za-z0-9+/=]{60,}/.test(val);
};

const findBase64ImageInObject = (obj, depth = 0) => {
  if (!obj || typeof obj !== 'object' || depth > 3) return null;
  for (const value of Object.values(obj)) {
    if (typeof value === 'string' && looksLikeBase64(value)) return value;
    if (value && typeof value === 'object') {
      const found = findBase64ImageInObject(value, depth + 1);
      if (found) return found;
    }
  }
  return null;
};

const buildImageSrc = (desc) => {
  if (!desc) return null;
  const storedUrl =
    desc._imageUrl ||
    desc._imageReqUrl ||
    desc._imageWipoUrl ||
    desc._yandexImageUrl;
  if (storedUrl) return storedUrl;

  const pick = (val) => {
    if (typeof val !== 'string') return null;
    if (val.startsWith('data:')) return val;
    if (looksLikeBase64(val)) {
      const mime = detectMimeFromBase64(val) || 'image/jpeg';
      return `data:${mime};base64,${val}`;
    }
    return null;
  };
  const byImage = pick(desc.image);
  const byImageReq = pick(desc.imageReq);
  const byGlobal = pick(desc.yandexImageSearch);
  let raw = byImage || byImageReq || byGlobal;
  if (!raw) {
    const nested = findBase64ImageInObject(desc);
    if (nested) raw = nested.startsWith('data:') ? nested : `data:${detectMimeFromBase64(nested)};base64,${nested}`;
  }
  return raw || null;
};

const renderParams = (desc) => {
  if (!desc || typeof desc !== 'object') return <Text type="secondary">Нет деталей</Text>;
  const tags = [];
  if (desc.name) tags.push(<Tag key="name" color="blue">Название: {desc.name}</Tag>);
  if (desc.requests && !desc.name) tags.push(<Tag key="requests" color="blue">Название: {desc.requests}</Tag>);
  if (desc.adress) tags.push(<Tag key="adress" color="purple">Адрес: {desc.adress}</Tag>);
  if (desc.adressReq && !desc.adress) tags.push(<Tag key="adressReq" color="purple">Адрес: {desc.adressReq}</Tag>);
  if (desc.patentObl) tags.push(<Tag key="patentObl" color="magenta">Правообладатель: {desc.patentObl}</Tag>);
  if (desc.patentOblRequests && !desc.patentObl) tags.push(<Tag key="patentOblRequests" color="magenta">Правообладатель: {desc.patentOblRequests}</Tag>);
  // WIPO параметры
  if (desc.wipoName) tags.push(<Tag key="wipoName" color="geekblue">WIPO Название: {desc.wipoName}</Tag>);
  if (desc.wipoAdres) tags.push(<Tag key="wipoAdres" color="geekblue">WIPO Адрес: {desc.wipoAdres}</Tag>);
  if (desc.wipoObl) tags.push(<Tag key="wipoObl" color="geekblue">WIPO Правообладатель: {desc.wipoObl}</Tag>);
  if (desc.konstruktor) tags.push(<Tag key="konstruktor" color="blue">Конструктор ТЗ: {desc.konstruktor}</Tag>);
  if (desc.konstruktor_req) tags.push(<Tag key="konstruktor_req" color="blue">Конструктор заявки: {desc.konstruktor_req}</Tag>);
  if (desc.konstruktor_wipo) tags.push(<Tag key="konstruktor_wipo" color="geekblue">Конструктор ВИПО: {desc.konstruktor_wipo}</Tag>);
  if (desc.description) tags.push(<Tag key="description" color="cyan">Описание: {String(desc.description).slice(0, 40)}</Tag>);
  if (desc.description_tz && !desc.description) tags.push(<Tag key="description_tz" color="cyan">Описание ТЗ: {String(desc.description_tz).slice(0, 40)}</Tag>);
  if (desc.description_req && !desc.description) tags.push(<Tag key="description_req" color="cyan">Описание заявки: {String(desc.description_req).slice(0, 40)}</Tag>);
  if (desc.description_wipo && !desc.description) tags.push(<Tag key="description_wipo" color="cyan">Описание ВИПО: {String(desc.description_wipo).slice(0, 40)}</Tag>);
  if (desc.description_elastic && !desc.description) tags.push(<Tag key="description_elastic" color="cyan">Описание: {String(desc.description_elastic).slice(0, 40)}</Tag>);
  if (Array.isArray(desc.mktu) && desc.mktu.length) {
    tags.push(
      <span key="mktu" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Text type="secondary">МКТУ:</Text>
        {desc.mktu.slice(0, 6).map((c) => (
          <Tag key={`mktu-${c}`} color="gold">{c}</Tag>
        ))}
        {desc.mktu.length > 6 && <Tag>+{desc.mktu.length - 6}</Tag>}
      </span>
    );
  }
  if (desc.quantity) tags.push(<Tag key="quantity" color="default">Кол-во: {desc.quantity}</Tag>);
  if (desc.imageName) tags.push(<Tag key="imageName" color="geekblue">Файл: {desc.imageName}</Tag>);
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{tags}</div>;
};

const isGlobalImageSearchDescription = (desc) =>
  !!(
    desc &&
    typeof desc === 'object' &&
    (desc.yandexImageSearch || desc._yandexImageUrl)
  );

const isGlobalImageSearchGroup = (group) =>
  isGlobalImageSearchDescription(group?.description) ||
  (group?.tasks || []).some((t) => isGlobalImageSearchDescription(t?.description));

const RequestsHistory = () => {
  const { getTasksFromServer, getGlobalSearchTaskDetail } = useSocketContext();
  const tasks = useSelector((s) => s.tasks.tasks);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Группируем задачи по времени и типу поиска
  const groupedTasks = React.useMemo(() => {
    if (!tasks || !tasks.length) return [];
    
    const groups = [];
    const processed = new Set();
    
    tasks.forEach((task, index) => {
      if (processed.has(task.id)) return;
      
      const group = {
        id: `group-${task.id}`,
        created_at: task.created_at,
        tasks: [task],
        mainTask: task,
        description: task.description,
      };
      
      // Ищем похожие задачи в пределах 1 секунды
      const taskTime = new Date(task.created_at).getTime();
      const similarTasks = tasks.filter((otherTask, otherIndex) => {
        if (otherIndex <= index || processed.has(otherTask.id)) return false;
        
        const otherTime = new Date(otherTask.created_at).getTime();
        const timeDiff = Math.abs(taskTime - otherTime);
        
        // Проверяем, что задачи в пределах 1 секунды и имеют похожие данные
        if (timeDiff > 1000) return false;
        
        const taskDesc = task.description || {};
        const otherDesc = otherTask.description || {};
        
        // Проверяем совпадение основных полей (кроме типа запроса)
        const taskQuery = taskDesc.name || taskDesc.requests || taskDesc.konstruktor || taskDesc.konstruktor_req || taskDesc.konstruktor_wipo || taskDesc.wipoName || taskDesc.wipoAdres || taskDesc.wipoObl || taskDesc.adress || taskDesc.adressReq || taskDesc.patentObl || taskDesc.patentOblRequests || '';
        const otherQuery = otherDesc.name || otherDesc.requests || otherDesc.konstruktor || otherDesc.konstruktor_req || otherDesc.konstruktor_wipo || otherDesc.wipoName || otherDesc.wipoAdres || otherDesc.wipoObl || otherDesc.adress || otherDesc.adressReq || otherDesc.patentObl || otherDesc.patentOblRequests || '';
        
        return taskQuery === otherQuery && 
               taskDesc.quantity === otherDesc.quantity &&
               JSON.stringify(taskDesc.mktu || []) === JSON.stringify(otherDesc.mktu || []);
      });
      
      // Добавляем найденные похожие задачи в группу
      similarTasks.forEach(similarTask => {
        group.tasks.push(similarTask);
        processed.add(similarTask.id);
      });
      
      processed.add(task.id);
      groups.push(group);
    });
    
    return groups.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [tasks]);

  const fetchTasks = useCallback(() => {
    setLoading(true);
    getTasksFromServer(limit, () => setLoading(false));
  }, [getTasksFromServer, limit]);

  const openGlobalSearchDetail = (taskId) => {
    setDetailOpen(true);
    setDetailTask(null);
    setDetailLoading(true);
    getGlobalSearchTaskDetail(taskId, (err, task) => {
      setDetailLoading(false);
      if (!err && task) {
        setDetailTask(task);
      } else {
        Modal.error({ title: 'Не удалось загрузить задачу', content: err || '' });
        setDetailOpen(false);
      }
    });
  };

  const detailReq = detailTask ? pickYandexImageSearchRequest(detailTask) : null;
  const detailPayload = extractGlobalSearchResultPayload(detailReq?.result);
  const detailHasContent = hasGlobalSearchReportContent(detailPayload);
  const detailCompleted =
    detailReq?.pipeline_stage === 'completed' ||
    detailReq?.status === 'completed' ||
    detailReq?.status === 'success';

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return (
    <Card
      size="small"
      title="История запросов"
      extra={
        <Space>
          <span>Показать</span>
          <Select
            size="small"
            value={limit}
            style={{ width: 90 }}
            onChange={setLimit}
            options={[
              { value: 10, label: '10' },
              { value: 20, label: '20' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
            ]}
          />
          <Button size="small" type="primary" onClick={fetchTasks} loading={loading}>
            Запросить
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : groupedTasks && groupedTasks.length > 0 ? (
        <List
          itemLayout="vertical"
          dataSource={groupedTasks}
          renderItem={(group) => {
            const imgSrc = buildImageSrc(group.description);
            const extra = imgSrc ? (
              <Image
                alt="preview"
                src={imgSrc}
                width={96}
                height={96}
                style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
                preview={{ src: imgSrc, mask: 'Открыть' }}
              />
            ) : null;
            
            const isGlobalSearch = isGlobalImageSearchGroup(group);
            const groupLabel = getGroupLabelFromTasks(group.tasks);

            const subTypes = group.tasks
              .map((t) => formatRequestTypeDetailed(detectRequestTypeFromDescription(t.description)))
              .join(', ');
            
            const globalTaskId = isGlobalSearch ? group.mainTask?.id : null;

            return (
              <List.Item key={group.id} extra={extra}>
                <List.Item.Meta
                  title={
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {isGlobalSearch && <GlobalOutlined style={{ color: '#1890ff' }} />}
                      <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                        {groupLabel}
                      </Text>
                      <Text type="secondary" className="theme-badge-muted" style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px' }}>
                        {group.tasks.length} {group.tasks.length === 1 ? 'запрос' : group.tasks.length < 5 ? 'запроса' : 'запросов'}
                      </Text>
                      {group.tasks.some(t => t.status === 'error') && <Tag color="red">Ошибка</Tag>}
                      {group.tasks.every(t => t.status === 'completed' || t.status === 'success') && <Tag color="green">Завершено</Tag>}
                      {/* {group.tasks.some(t => !['completed', 'success', 'error'].includes(t.status)) && <Tag color="orange">В процессе</Tag>} */}
                      {group.created_at && <Text type="secondary">{new Date(group.created_at).toLocaleString()}</Text>}
                      {isGlobalSearch && globalTaskId != null && (
                        <Button type="link" size="small" onClick={() => openGlobalSearchDetail(globalTaskId)}>
                          Результат
                        </Button>
                      )}
                    </div>
                  }
                  description={
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Типы: {subTypes}
                        </Text>
                      </div>
                      {renderParams(group.description)}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      ) : (
        <Empty description="Нет последних поисковых запросов" />
      )}

      <Modal
        title={
          detailCompleted && detailHasContent
            ? 'Отчёт глобального поиска'
            : 'Задача глобального поиска'
        }
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={960}
        bodyStyle={{ maxHeight: 'min(85vh, 900px)', overflowY: 'auto', paddingTop: 12 }}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : detailTask ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div className="global-search-report__meta">
              <Space wrap size={[8, 8]}>
                {detailCompleted && detailHasContent ? (
                  <Tag color="success">Завершено</Tag>
                ) : detailReq?.pipeline_stage ? (
                  <Tag color="processing">
                    {GLOBAL_SEARCH_PIPELINE_LABELS[detailReq.pipeline_stage] || detailReq.pipeline_stage}
                  </Tag>
                ) : null}
                <Text type="secondary">
                  Задача {detailTask.id} · {formatGlobalSearchDate(detailTask.created_at)}
                </Text>
                {detailReq?.status ? <Text type="secondary">Очередь: {detailReq.status}</Text> : null}
              </Space>
            </div>

            {detailHasContent ? (
              <GlobalSearchResultReport payload={detailPayload} dense />
            ) : !detailLoading && detailReq ? (
              <Empty description="Результат ещё не готов или недоступен" />
            ) : null}
          </Space>
        ) : null}
      </Modal>
    </Card>
  );
};

export default RequestsHistory;


